import React, { useState } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { A11yWrapper } from 'components/a11y-wrapper';
import Currency from 'components/currency';
import { ServiceMode } from 'generated/graphql-gateway';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { DeliveryFeesModal } from 'pages/cart/delivery-fees-modal';
import { IconContainer } from 'pages/cart/your-cart/styled';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { computeDeliveryFee } from 'utils/cart/helper';

import { Total, TotalLabel, TotalsRow } from './styled';
import { TaxesRow } from './taxes-row';

interface TaxesAndFeesProps {
  deliveryFee: number | null;
  deliveryFeeDiscount: number | null;
  dropoffState?: string | null;
  serviceFeeCents: number;
  smallCartFeeCents: number;
  geographicalFeeCents: number;
  taxCents: number;
  serviceMode: ServiceMode | null;
  subTotalCents: number;
  isLoading?: boolean;
}

const TaxesAndFees: React.FC<React.PropsWithChildren<TaxesAndFeesProps>> = ({
  isLoading,
  deliveryFee,
  deliveryFeeDiscount,
  dropoffState,
  serviceFeeCents,
  smallCartFeeCents,
  geographicalFeeCents,
  taxCents,
  serviceMode,
  subTotalCents,
}) => {
  const [showDeliveryFeesModal, setShowDeliveryFeesModal] = useState(false);
  const { formatMessage } = useIntl();

  const finalDeliveryFee = computeDeliveryFee({
    feeCents: deliveryFee,
    feeDiscountCents: deliveryFeeDiscount,
    serviceFeeCents,
    smallCartFeeCents,
    geographicalFeeCents,
  });

  const isSingleDeliveryFee = !serviceFeeCents && !smallCartFeeCents && !geographicalFeeCents;

  const onPressDeliveryFees = () => {
    setShowDeliveryFeesModal(true);
  };

  const totalFees = geographicalFeeCents + smallCartFeeCents + serviceFeeCents + finalDeliveryFee;

  return (
    <>
      <TotalsRow mb={'$3'}>
        {isLoading ? (
          <CurrencyLineSkeleton />
        ) : (
          isSingleDeliveryFee && (
            <>
              <A11yWrapper>
                <TotalLabel>{formatMessage({ id: 'deliveryFee' })}</TotalLabel>
              </A11yWrapper>
              <Total testID="delivery-fee-amount">
                <Currency amount={finalDeliveryFee} textProps={{ fontSize: 'md' }} />
              </Total>
            </>
          )
        )}
      </TotalsRow>
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <TotalsRow mb={'$3'} accessible justifyContent="space-between">
          <Box flexDirection="row">
            <TotalLabel>{formatMessage({ id: 'deliveryFees' })}</TotalLabel>
            <IconContainer
              padding={0}
              accessibilityLabel={formatMessage({
                id: 'taxesAndFeesButtonAriaLabel',
              })}
              onPress={onPressDeliveryFees}
            >
              <Icon
                variant="info"
                paddingLeft="$0.5"
                size="$5"
                {...hiddenAccessibilityPlatformProps}
              />
            </IconContainer>
          </Box>
          <Total>
            <Currency textProps={{ fontSize: 'md' }} amount={totalFees} />
          </Total>
        </TotalsRow>
      )}
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <TaxesRow
          textProps={{ fontSize: 'md' }}
          mb={'$3'}
          taxCents={taxCents}
          isLoading={isLoading}
        />
      )}
      <DeliveryFeesModal
        open={showDeliveryFeesModal}
        onClose={() => setShowDeliveryFeesModal(false)}
        serviceMode={serviceMode}
        subTotalCents={subTotalCents}
        deliveryFeeCents={finalDeliveryFee}
        serviceFeeCents={serviceFeeCents}
        geographicalFeeCents={geographicalFeeCents}
        smallCartFeeCents={smallCartFeeCents}
        isLoading={isLoading}
      />
    </>
  );
};

export default TaxesAndFees;
